import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { FileText, Trash2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SavedReportsModal = ({ open, onClose }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const reload = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/pi-counts/reports`);
      setReports(res.data?.reports || []);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) reload(); }, [open]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api.delete(`${API}/pi-counts/reports/${id}`);
      setReports(prev => prev.filter(r => r.id !== id));
      toast.success('Report deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="saved-reports-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-violet-700">
            <FileText className="w-5 h-5" />
            Saved Reports
          </DialogTitle>
          <DialogDescription>
            Click a report to open it. Reports are scoped to the current branch.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          {loading && <p className="text-sm text-gray-500 py-4 text-center">Loading…</p>}
          {!loading && reports.length === 0 && (
            <p className="text-sm text-gray-500 py-8 text-center">No saved reports yet.</p>
          )}
          <div className="divide-y">
            {reports.map(r => (
              <div key={r.id} className="flex items-center justify-between py-3 gap-3" data-testid={`saved-report-row-${r.id}`}>
                <button
                  onClick={() => { onClose?.(); navigate(`/pi-counts/reports/${r.id}`); }}
                  className="flex-1 text-left hover:bg-violet-50 rounded-lg px-2 py-1 transition-colors"
                >
                  <p className="font-semibold text-gray-900">{r.name}</p>
                  <p className="text-xs text-gray-500">
                    {r.start_date} → {r.end_date} · {r.stats?.total_suppliers || 0} supplier(s) · {r.stats?.total_counts || 0} count(s)
                  </p>
                </button>
                <button onClick={() => navigate(`/pi-counts/reports/${r.id}`)} className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg" title="Open">
                  <ExternalLink className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(r.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Delete" data-testid={`saved-report-delete-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SavedReportsModal;
